import { Collapse } from "@blueprintjs/core";
import { Copyright } from "@package/ui-components";
import { type SidebarEntry } from "@package/ui-config";
import { AccountMenu, AppSwitcher, SidebarNavButton } from "@package/ui-header";
import { OrganizationSelect } from "@package/ui-organization";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { sidebarEntries } from "../../constants/contants";
import { SidebarMenuItem } from "./SidebarMenuItem";

const SIDEBAR_ENTRIES_ADVANCED: SidebarEntry[] = [sidebarEntries.layout];
const SIDEBAR_ENTRIES_ANALYTICS: SidebarEntry[] = [sidebarEntries.events];

function SidebarSectionLabel({ children }: { children: string }) {
  return (
    <p className="px-2.5 mt-4 mb-1 text-[11px] font-semibold tracking-wide text-brand-paper/40 uppercase">
      {children}
    </p>
  );
}

export function Sidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isEntryActiveAdvanced = useMemo<boolean>(
    () =>
      SIDEBAR_ENTRIES_ADVANCED.some(
        (entry) => !!pathname?.startsWith(`/${entry.path}`),
      ),
    [pathname],
  );
  const isEntryActiveAnalytics = useMemo<boolean>(
    () =>
      SIDEBAR_ENTRIES_ANALYTICS.some(
        (entry) => !!pathname?.startsWith(`/${entry.path}`),
      ),
    [pathname],
  );

  const [isEntryOpenAdvanced, setIsEntryOpenAdvanced] = useState(
    isEntryActiveAdvanced,
  );
  const [isEntryOpenAnalytics, setIsEntryOpenAnalytics] = useState(
    isEntryActiveAnalytics,
  );

  useEffect(
    function effectEntryActiveAdvanced() {
      setIsEntryOpenAdvanced(isEntryActiveAdvanced);
    },
    [isEntryActiveAdvanced],
  );

  return (
    <div className="bp6-dark flex flex-col h-full w-60 bg-brand-navy">
      <div className="flex items-center justify-center h-32 border-b border-white/10">
        <img src="/panoscape-logo.png" alt="PanoScape" className="h-24 w-24" />
      </div>
      <div className="px-2.5 pt-3">
        <OrganizationSelect onChange={() => navigate("/")} />
      </div>
      <nav
        aria-label="Primary"
        className="flex-grow px-2.5 pb-2 overflow-y-auto"
      >
        <SidebarSectionLabel>Manage</SidebarSectionLabel>
        <div className="flex flex-col gap-0.5">
          <SidebarMenuItem entry={sidebarEntries.locations} />
          <SidebarMenuItem entry={sidebarEntries.profiles} />
          <SidebarMenuItem entry={sidebarEntries.posts} />
          <SidebarMenuItem entry={sidebarEntries.tags} />
          <SidebarMenuItem entry={sidebarEntries.media} />
          <SidebarMenuItem entry={sidebarEntries.scheduler} />
        </div>

        <SidebarSectionLabel>Insights</SidebarSectionLabel>
        <SidebarNavButton
          icon="pie-chart"
          text="Analytics"
          trailingIcon={isEntryOpenAnalytics ? "caret-down" : "caret-right"}
          onClick={() => setIsEntryOpenAnalytics(!isEntryOpenAnalytics)}
        />
        <Collapse isOpen={isEntryOpenAnalytics}>
          <div className="flex flex-col gap-0.5 mt-0.5">
            {SIDEBAR_ENTRIES_ANALYTICS.map((entry) => (
              <SidebarMenuItem key={entry.path} entry={entry} />
            ))}
          </div>
        </Collapse>

        <SidebarSectionLabel>Admin</SidebarSectionLabel>
        <SidebarNavButton
          icon="menu"
          text="Advanced"
          trailingIcon={isEntryOpenAdvanced ? "caret-down" : "caret-right"}
          onClick={() => setIsEntryOpenAdvanced(!isEntryOpenAdvanced)}
        />
        <Collapse isOpen={isEntryOpenAdvanced}>
          <div className="flex flex-col gap-0.5 mt-0.5">
            {SIDEBAR_ENTRIES_ADVANCED.map((entry) => (
              <SidebarMenuItem key={entry.path} entry={entry} />
            ))}
          </div>
        </Collapse>
      </nav>
      <div className="flex flex-col gap-0.5 px-2.5 pt-2 pb-1 border-t border-white/10">
        <AppSwitcher />
        <AccountMenu variant="full" />
      </div>
      <div className="mt-2 ml-1 text-xs pb-4 px-2.5">
        <Copyright slim />
      </div>
    </div>
  );
}
